import { computed, inject, Injectable, signal } from '@angular/core';
import { UserInfo } from './user-info.model';
import { COMMON_ENVIRONMENT } from '@lib/core/environment/common-environment';

@Injectable({
  providedIn: 'root',
})
export abstract class AuthService {
  private readonly commonEnvironment = inject(COMMON_ENVIRONMENT);

  protected static readonly TOKEN_KEY = 'token';

  protected _userInfo = signal<UserInfo | undefined>(undefined);
  public readonly userInfo = computed(this._userInfo);

  public userName = computed<string>(() => {
    const userInfo = this.userInfo();
    return userInfo ? `${userInfo.givenName} ${userInfo.sn}` : '';
  });

  // todo remove isBo when separate fiche consultant in BO and consultant component
  public isBo = computed<boolean>(() => {
    const rawMemberOf = this._userInfo()?.memberOf;
    const memberOf = Array.isArray(rawMemberOf)
      ? rawMemberOf
      : rawMemberOf
        ? [rawMemberOf]
        : [];
    const isGcom = memberOf.some(member =>
      this.commonEnvironment.roles.gcomAdGroups.some(group => member.includes(group))
    );
    const isShm = memberOf.some(member =>
      this.commonEnvironment.roles.shmAdGroups.some(group => member.includes(group))
    );
    return isGcom || isShm;
  });
  public isOnlyGcom = computed<boolean>(() => {
    const rawMemberOf = this._userInfo()?.memberOf;
    const memberOf = Array.isArray(rawMemberOf)
      ? rawMemberOf
      : rawMemberOf
        ? [rawMemberOf]
        : [];
    const isGcom = memberOf.some(member =>
      this.commonEnvironment.roles.gcomAdGroups.some(group => member.includes(group))
    );
    const isShm = memberOf.some(member =>
      this.commonEnvironment.roles.shmAdGroups.some(group => member.includes(group))
    );
    return isGcom && !isShm;
  });

  public async getToken(): Promise<string | null> {
    // TODO refresh
    return sessionStorage.getItem(AuthService.TOKEN_KEY);
  }

  protected setToken(token: string | null) {
    if (token === null) {
      sessionStorage.removeItem(AuthService.TOKEN_KEY);
    } else {
      sessionStorage.setItem(AuthService.TOKEN_KEY, token);
    }
  }

  abstract login(): UserInfo | undefined;

  abstract logout(): void;

  protected parseToken(): UserInfo {
    const token = sessionStorage.getItem(AuthService.TOKEN_KEY);
    const payload = token?.split('.')[1];

    if (!payload) {
      throw new Error('Cannot parse JWT');
    }

    return this.jwtDecode(payload);
  }

  protected jwtDecode(tokenStrPayload: string) {
    return JSON.parse(
      this.b64DecodeUnicode(tokenStrPayload.replace('-', '+').replace('_', '/'))
    );
  }

  protected b64DecodeUnicode(str: string) {
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(str), c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  }
}
